// Small, pure helpers the Activity screens share. No React here.
//
// The server sends each event as one sentence already, so nothing here
// invents wording: these turn a moment into "2 hours ago", and the rest of an
// event into the handful of labelled lines the details drawer shows. A raw id
// is never one of them - only the key prefix, which names a key without being
// usable as one, and the request reference, which is what support asks for.

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

const parse = (value) => {
  const date = value ? new Date(value) : null;
  return date && !isNaN(date.getTime()) ? date : null;
};

// "Just now", "12 minutes ago", "3 hours ago", "Yesterday", "4 days ago",
// then the date itself - a month-old event is easier to place by its date.
export const relativeTime = (value, now = Date.now()) => {
  const date = parse(value);
  if (!date) return "";
  const gap = now - date.getTime();
  if (gap < 0) return "Just now";
  if (gap < MINUTE) return "Just now";
  if (gap < HOUR) {
    const minutes = Math.floor(gap / MINUTE);
    return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  }
  if (gap < DAY) {
    const hours = Math.floor(gap / HOUR);
    return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  }
  const days = Math.floor(gap / DAY);
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} days ago`;
  return date.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
};

// The whole moment, for the tooltip on the relative time and for the drawer.
export const exactTime = (value) => {
  const date = parse(value);
  if (!date) return "";
  return date.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// The day an event happened, for the heading a list groups under.
export const dayOf = (value) => {
  const date = parse(value);
  return date ? date.toISOString().slice(0, 10) : "";
};

export const dayHeading = (day, now = Date.now()) => {
  const date = parse(`${day}T12:00:00`);
  if (!date) return "";
  const today = new Date(now).toISOString().slice(0, 10);
  const yesterday = new Date(now - DAY).toISOString().slice(0, 10);
  if (day === today) return "Today";
  if (day === yesterday) return "Yesterday";
  return date.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
};

export const OUTCOME_LABELS = { done: "Done", refused: "Refused" };

// The events of a page, in the order they arrived, under the day they
// happened on.
export const groupByDay = (events = []) => {
  const days = [];
  events.forEach((event) => {
    const day = dayOf(event.at);
    const last = days[days.length - 1];
    if (last && last.day === day) last.events.push(event);
    else days.push({ day, events: [event] });
  });
  return days;
};

// What the details drawer lists: a definition list, never a dump of the
// record. Anything the server didn't send is left out rather than shown empty.
export const detailRows = (event = {}) =>
  [
    { label: "What happened", value: event.categoryLabel },
    { label: "Outcome", value: OUTCOME_LABELS[event.outcome] },
    { label: "When", value: exactTime(event.at) },
    { label: "Who", value: event.actor },
    { label: "Workspace", value: event.workspace },
    { label: "Database", value: event.database },
    { label: "What it was about", value: event.subject },
    { label: "Why", value: event.reason },
    { label: "API key", value: event.keyPrefix ? `bfb_live_${event.keyPrefix}_…` : null },
    { label: "Came from", value: event.ip },
    { label: "Request reference", value: event.requestId },
  ].filter((row) => row.value);

// The date range behind the "When" filter. Everything but "custom" is a
// number of days back from today; "any" leaves it to the server, which looks
// at its own window.
export const PERIODS = [
  { key: "7", label: "Last 7 days", days: 7 },
  { key: "30", label: "Last 30 days", days: 30 },
  { key: "90", label: "Last 90 days", days: 90 },
  { key: "custom", label: "A date range" },
];

const isoDay = (time) => new Date(time).toISOString().slice(0, 10);

export const rangeFor = (period, custom = {}, now = Date.now()) => {
  if (period === "custom") return { from: custom.from || "", to: custom.to || "" };
  const chosen = PERIODS.find((item) => item.key === period);
  if (!chosen || !chosen.days) return { from: "", to: "" };
  return { from: isoDay(now - (chosen.days - 1) * DAY), to: "" };
};
